import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { KeenIcon } from '@/components';
import { useAbility } from '@/providers/';
import contactService from '@/services/contact/contactService';

// Matches ContactIdPhotoRequest on the server.
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/**
 * The client's ID / driver's license photo (OL-135), shown on the contact and on
 * each of their matters. The image is kept in local state so neither parent
 * page has to refetch after a change.
 */
const ContactIdPhoto = ({ contact, loading = false }) => {
  const { ability } = useAbility();
  const canEdit = ability.can('handle', 'contact');
  const [photo, setPhoto] = useState(contact?.id_photo || null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPhoto(contact?.id_photo || null);
  }, [contact?.id, contact?.id_photo]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    // Reset first so picking the same file twice still fires onChange.
    event.target.value = '';
    if (!file || !contact?.id) return;

    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error('That file type is not supported.', { description: 'Choose a JPG, PNG or WebP image.' });
      return;
    }

    if (file.size > PHOTO_MAX_BYTES) {
      toast.error('That image is too large.', { description: 'ID photos must be 5 MB or smaller.' });
      return;
    }

    setBusy(true);
    try {
      const { data, status } = await contactService.uploadIdPhoto(contact.id, file);
      if (status) {
        setPhoto(data.id_photo || null);
        toast.success('Client ID saved.');
      }
    } catch (error) {
      toast.error('Could not save the ID photo.', {
        description: error?.errors?.id_photo?.[0] || error?.message || 'Please try again in a moment.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!contact?.id) return;

    setBusy(true);
    try {
      const { status } = await contactService.deleteIdPhoto(contact.id);
      if (status) {
        setPhoto(null);
        setPreview(false);
        toast.success('Client ID removed.');
      }
    } catch (error) {
      toast.error('Could not remove the ID photo.', {
        description: error?.message || 'Please try again in a moment.',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!loading && !contact?.id) return null;

  return (
    <>
      <div className="card lg:w-[280px] w-full shrink-0 h-fit">
        <div className="card-header">
          <h3 className="card-title">Client ID</h3>
          {canEdit && photo && (
            <button
              type="button"
              className="btn btn-xs btn-light text-danger"
              onClick={handleRemove}
              disabled={busy}
              title="Remove the ID photo"
            >
              <KeenIcon icon="trash" />
              Remove
            </button>
          )}
        </div>

        <div className="card-body flex flex-col items-center gap-3 py-5">
          {loading ? (
            <div className="h-[160px] w-full rounded-md bg-gray-100 animate-pulse" />
          ) : photo ? (
            <button
              type="button"
              onClick={() => setPreview(true)}
              className="w-full rounded-md border border-gray-200 bg-light overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              title="View full size"
            >
              <img src={photo} alt="Client ID" className="max-h-[160px] w-full object-contain" />
            </button>
          ) : (
            <div className="flex flex-col items-center gap-1 py-6 text-xs text-gray-500">
              <KeenIcon icon="picture" className="text-3xl text-gray-400" />
              No ID or license on file
            </div>
          )}

          {canEdit && !loading && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-light w-full justify-center"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <KeenIcon icon="exit-up" />}
                {photo ? 'Replace photo' : 'Upload ID or license'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileChange}
              />
            </>
          )}
        </div>
      </div>

      {preview && photo && (
        <div className="modal-wrapper modal-animate-fade" onClick={() => setPreview(false)}>
          <div className="modal-body modal-animate-scale w-4/5 max-w-3xl" onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setPreview(false)} className="modal-close-btn" aria-label="Close">
              <KeenIcon icon="cross" />
            </button>
            <h2 className="text-lg font-medium text-gray-900 mb-3">Client ID</h2>
            <img src={photo} alt="Client ID, full size" className="w-full max-h-[75vh] object-contain rounded-md" />
          </div>
        </div>
      )}
    </>
  );
};

export { ContactIdPhoto };
